import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { AppDispatch } from '../../../store/store';
import { getStudents, StudentListItem } from '../../../verticals/iqmax/api/students';
import { KID_MODE_CODES } from '../api/context';
import { enterKidMode } from '../store/contextSlice';
import { KID_HOME } from '../components/KidModeGate';

type Load =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; students: StudentListItem[] };

/**
 * GAP-S01-01 — where a parent hands the device to a child.
 *
 * The learner list is the parent's own; the server checks ownership and consent
 * again on entry (DEC-S2-S01-03). There is nothing to set up first: the way back
 * is the account's own password (DEC-S2-S01-04).
 */
const KidModeSetup: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [load, setLoad] = useState<Load>({ status: 'loading' });

  const [entering, setEntering] = useState<string | null>(null);
  const [enterError, setEnterError] = useState<string | null>(null);

  const fetchAll = useCallback(() => {
    setLoad({ status: 'loading' });
    getStudents()
      .then((students) => setLoad({ status: 'ready', students }))
      .catch(() => setLoad({ status: 'error' }));
  }, []);

  useEffect(fetchAll, [fetchAll]);

  const open = async (studentId: string) => {
    setEntering(studentId);
    setEnterError(null);
    const result = await dispatch(enterKidMode(studentId));
    setEntering(null);
    if (enterKidMode.fulfilled.match(result)) {
      navigate(KID_HOME, { replace: true });
      return;
    }
    const code = enterKidMode.rejected.match(result) ? result.payload?.code : undefined;
    setEnterError(
      code === KID_MODE_CODES.consentRequired
        ? 'Para abrir su espacio, primero acepta el aviso de grabación de una inscripción de este alumno.'
        : 'No pudimos abrir el espacio del alumno. Inténtalo otra vez.',
    );
  };

  if (load.status === 'loading') {
    return <p role="status">Cargando…</p>;
  }
  if (load.status === 'error') {
    return (
      <div role="alert">
        <p>No pudimos cargar el modo niño.</p>
        <button className="btn" onClick={fetchAll}>
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="page">
      <h1>Modo niño</h1>
      <p>
        Abre el espacio de un alumno en este dispositivo. Mientras está abierto, aquí nadie puede
        comprar, cambiar de alumno ni ver otras cuentas sin la contraseña de tu cuenta. Tus otros
        dispositivos siguen siendo tuyos.
      </p>

      <section aria-labelledby="learner-title">
        <h2 id="learner-title">¿Quién va a usar el dispositivo?</h2>
        {load.students.length === 0 ? (
          <p>Todavía no tienes alumnos.</p>
        ) : (
          <ul>
            {load.students.map((s) => (
              <li key={s.studentId}>
                <button
                  type="button"
                  className="btn"
                  disabled={entering !== null}
                  onClick={() => open(s.studentId)}
                >
                  Abrir el espacio de {s.firstName}
                </button>
              </li>
            ))}
          </ul>
        )}
        {enterError && <p role="alert">{enterError}</p>}
      </section>
    </div>
  );
};

export default KidModeSetup;
